import { Form, Input, Button, Card, message, Tabs, Tooltip } from 'antd';
import { LockOutlined, UserOutlined, MobileOutlined, SafetyOutlined, PictureOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tenantApi } from '../../api/tenant';
import { useTenantAuth } from '../../stores/auth.tenant';

type LoginMode = 'password' | 'sms';

const PHONE_REGEX = /^1[3-9]\d{9}$/;

interface CaptchaState {
  id: string;
  image: string; // data URL
}

export default function TenantLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useTenantAuth(s => s.setAuth);
  const [mode, setMode] = useState<LoginMode>('password');
  const [loading, setLoading] = useState(false);

  const [smsForm] = Form.useForm<{ phone: string; captcha_text: string; code: string }>();
  const [sendingCode, setSendingCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [captcha, setCaptcha] = useState<CaptchaState | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const refreshCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const { captcha_id, image_base64 } = await tenantApi.getCaptcha();
      const image = image_base64.startsWith('data:') ? image_base64 : `data:image/png;base64,${image_base64}`;
      setCaptcha({ id: captcha_id, image });
      smsForm.setFieldValue('captcha_text', '');
    } catch {
      message.error('图形验证码加载失败');
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'sms' && !captcha) refreshCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const goAfterAuth = (mustChange: boolean) => {
    if (mustChange) {
      navigate('/change-password', { replace: true });
      return;
    }
    const redirect = (location.state as any)?.from?.pathname || '/dashboard';
    navigate(redirect, { replace: true });
  };

  const handleAuthError = (e: any) => {
    const code = e.response?.data?.error_code;
    const errMap: Record<string, string> = {
      invalid_credentials: '账号或密码错误',
      'invalid credentials': '账号或密码错误',
      invalid_code: '验证码错误或已过期',
      code_expired: '验证码已过期，请重新获取',
      phone_not_registered: '该手机号未注册租户管理员',
      sms_send_failed: '验证码发送失败，请稍后再试',
      sms_rate_limited: '发送过于频繁，请稍后再试',
      invalid_captcha: '图形验证码错误',
      captcha_expired: '图形验证码已过期，请刷新',
    };
    message.error(errMap[code] || '登录失败');
    if (code === 'invalid_captcha' || code === 'captcha_expired') refreshCaptcha();
  };

  const onPasswordLogin = async (v: { username: string; password: string }) => {
    setLoading(true);
    try {
      const { jwt, user, tenant, has_seat, must_change_password } = await tenantApi.login(v.username, v.password);
      setAuth(jwt, user, tenant, has_seat, must_change_password);
      goAfterAuth(must_change_password);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };

  const onSendSmsCode = async () => {
    try {
      const { phone, captcha_text } = await smsForm.validateFields(['phone', 'captcha_text']);
      if (!captcha) {
        message.warning('请等待图形验证码加载');
        return;
      }
      setSendingCode(true);
      await tenantApi.sendSmsCode(phone, captcha.id, captcha_text);
      message.success('验证码已发送');
      setCooldown(60);
    } catch (e: any) {
      if (e?.errorFields) return;
      handleAuthError(e);
    } finally {
      setSendingCode(false);
    }
  };

  const onSmsLogin = async (v: { phone: string; code: string }) => {
    setLoading(true);
    try {
      const { jwt, user, tenant, has_seat, must_change_password } = await tenantApi.loginByPhone(v.phone, v.code);
      setAuth(jwt, user, tenant, has_seat, must_change_password);
      goAfterAuth(must_change_password);
    } catch (e: any) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };

  const captchaImg = (
    <Tooltip title="点击刷新">
      <div
        onClick={refreshCaptcha}
        style={{
          width: 110,
          height: 38,
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          background: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {captchaLoading || !captcha ? (
          <ReloadOutlined spin={captchaLoading} style={{ color: '#999' }} />
        ) : (
          <img src={captcha.image} alt="captcha" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </div>
    </Tooltip>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
      <Card style={{ width: 400 }} title="元睿 AI · 企业后台">
        {/*
          多方式登录功能开关。当前后端仅实现「账号密码」，手机验证码待后端就绪：
          - GET  /api/tenant/admin/auth/captcha
          - POST /api/tenant/admin/auth/sms/send
          - POST /api/tenant/admin/auth/login-by-phone
          后端就绪后把下方常量改成 true 即可恢复双 tab。smsForm / captcha hooks
          全部保留不动（dead code 由 tree-shake 自动剔除）。
        */}
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {(() => {
          // TODO: 后端 SMS 登录接口实现状态未确认。上线 SMS 登录前需确认后端三个端点
          // （发送验证码 POST /sms/send / 验证码登录 POST /login-by-phone / 绑定手机）已实现，
          // 再将此 flag 改为 true。
          const FEATURE_SMS_LOGIN = false;
          const passwordTab = {
            key: 'password',
            label: '账号密码登录',
            children: (
              <Form onFinish={onPasswordLogin} layout="vertical">
                {/* 登录标识统一对外为手机号（后端按 username/phone 双查兜底）；仍兼容历史自定义用户名 */}
                <Form.Item name="username" rules={[{ required: true, min: 3, message: '请输入手机号' }]}>
                  <Input prefix={<UserOutlined />} placeholder="手机号" autoFocus />
                </Form.Item>
                <Form.Item name="password" rules={[{ required: true, min: 6, message: '请输入密码' }]}>
                  <Input.Password prefix={<LockOutlined />} placeholder="密码" />
                </Form.Item>
                <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
              </Form>
            ),
          };
          const smsTab = {
            key: 'sms',
            label: '手机验证码登录',
            children: (
                <Form form={smsForm} onFinish={onSmsLogin} layout="vertical">
                  <Form.Item
                    name="phone"
                    rules={[
                      { required: true, message: '请输入手机号' },
                      { pattern: PHONE_REGEX, message: '手机号格式不正确' },
                    ]}
                  >
                    <Input prefix={<MobileOutlined />} placeholder="手机号" maxLength={11} />
                  </Form.Item>
                  <Form.Item
                    name="captcha_text"
                    rules={[
                      { required: true, message: '请输入图形验证码' },
                      { min: 4, max: 6, message: '请输入图形验证码' },
                    ]}
                  >
                    <Input
                      prefix={<PictureOutlined />}
                      placeholder="图形验证码"
                      maxLength={6}
                      addonAfter={captchaImg}
                    />
                  </Form.Item>
                  <Form.Item
                    name="code"
                    rules={[
                      { required: true, message: '请输入短信验证码' },
                      { len: 6, message: '验证码为 6 位数字' },
                    ]}
                  >
                    <Input
                      prefix={<SafetyOutlined />}
                      placeholder="短信验证码"
                      maxLength={6}
                      addonAfter={
                        <Button
                          type="link"
                          size="small"
                          loading={sendingCode}
                          disabled={cooldown > 0}
                          onClick={onSendSmsCode}
                          style={{ padding: 0 }}
                        >
                          {cooldown > 0 ? `${cooldown}s 后重发` : '获取验证码'}
                        </Button>
                      }
                    />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
                </Form>
              ),
          };
          const items = [passwordTab, ...(FEATURE_SMS_LOGIN ? [smsTab] : [])];
          return (
            <Tabs
              activeKey={mode}
              onChange={(k: string) => setMode(k as LoginMode)}
              centered
              items={items}
              renderTabBar={items.length === 1 ? () => <></> : undefined}
            />
          );
        })()}
      </Card>
    </div>
  );
}
